import { observer } from 'mobx-react-lite';
import { useEffect, useMemo } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { BasePage, PageType } from 'src/components/BasePage';
import {
    FormioCustomEventComponent,
    handleStartProcessCustomEvent,
} from 'src/components/EntityPresentation/utils/handleStartProcessCustomEvent';
import { FormioForm } from 'src/components/FormioForm/FormioForm';
import { SpinnerFullWidth } from 'src/components/Spinner/SpinnerFullWidth';
import { Router } from 'src/core/router/Router';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { Entity } from 'src/utils/mobx/BasicStore/BasicStore.types';
import { CombinedLoadingStatus } from 'src/utils/mobx/CombinedLoadingStatus';

type ListPageProps = {
    pageName: string;
    store: BasicStore<Entity>;
    className?: string;
};

export const ListPage = observer(
    ({ pageName, store, className, ...props }: ListPageProps) => {
        const { url } = useRouteMatch();

        useEffect(() => {
            store.loadForm();
            store.loadList();

            return () => {
                store.formLoader.reset();
                store.listLoader.reset();
            };
        }, []);

        const loadingStatus = useMemo(() => {
            return new CombinedLoadingStatus([
                store.formLoader,
                store.listLoader,
            ]);
        }, []);

        if (loadingStatus.isLoading) {
            return <SpinnerFullWidth />;
        }

        return (
            <BasePage
                className={className}
                pageName={pageName}
                type={PageType.FULL}
                {...props}
            >
                <FormioForm
                    form={store.currentItemForm!}
                    submission={{ data: store.list }}
                    onCustomEvent={(event) => {
                        if (event.type === 'startProcess') {
                            handleStartProcessCustomEvent(event, url);
                        }

                        if (event.type === 'edit') {
                            Router.navigate(`${url}/${event.data.id}/edit`);
                        }

                        if (event.type === 'create') {
                            const entity = (
                                event.component as FormioCustomEventComponent
                            ).properties?.entity;
                            const queryParams = (
                                event.component as FormioCustomEventComponent
                            ).properties?.urlParams;
                            Router.navigate(`/${entity}/create?${queryParams}`);
                        }

                        return Promise.resolve();
                    }}
                />
            </BasePage>
        );
    },
);
